import { useState, useEffect, useCallback } from 'react';
import { Service, ServiceStatus, StatusKey, StatusMap, HistoryEntry } from '../types';
import { SERVICES } from '../constants';

export function useServiceStatus(): StatusMap {
  const [statuses, setStatuses] = useState<StatusMap>(() =>
    Object.fromEntries(
      SERVICES.map((s) => [
        s.id,
        { status: 'checking' as StatusKey, latency: null, lastChecked: null, history: [] },
      ])
    )
  );

  const checkService = useCallback(async (service: Service) => {
    const start = performance.now();
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 8000);
      await fetch(service.url, {
        method: 'HEAD',
        mode: 'no-cors',
        cache: 'no-store',
        signal: controller.signal,
      });
      clearTimeout(timeout);
      const latency = Math.round(performance.now() - start);
      const status: StatusKey = latency > 4000 ? 'degraded' : 'online';
      setStatuses((prev) => {
        const history: HistoryEntry[] = [
          ...(prev[service.id]?.history ?? []),
          { status, ts: Date.now() },
        ].slice(-20);
        return { ...prev, [service.id]: { status, latency, lastChecked: Date.now(), history } };
      });
    } catch {
      const latency = Math.round(performance.now() - start);
      setStatuses((prev) => {
        const history: any = [
          ...(prev[service.id]?.history ?? []),
          { status: 'offline', ts: Date.now() },
        ].slice(-20);
        return { ...prev, [service.id]: { status: 'offline', latency, lastChecked: Date.now(), history } };
      });
    }
  }, []);

  useEffect(() => {
    const run = () => SERVICES.forEach((s) => checkService(s));
    run();
    const interval = setInterval(run, 30000);
    return () => clearInterval(interval);
  }, [checkService]);

  return statuses;
}
